import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import { useQuery } from 'react-apollo';
import { useHistory } from 'react-router-dom';

import Alert from '../Alert';
import Spinner from '../Spinner';
import useAlbumSlideshow from '../../../apps/pro/views/Album/AlbumSlideshow/hooks/useAlbumSlideshow';
import PhoneForm from './components/PhoneForm';
import PinVerifyForm from './components/PinVerifyForm';
import NameForm from './components/NameForm';
import { getErrorString } from '../../../util/normalize-errors';
import useSlideshowOnboarding from './hooks/useSlideshowOnboarding';
import { MeQuery } from '../../../graphql/queries/account';
import SuccessForm from './components/SuccessForm';
import useGoogleRecaptcha from '../../../services/recaptcha/useGoogleRecaptcha';
import usePhoneAuth from '../../auth/hooks/usePhoneAuth';
import { parsePhone } from '../../../util/phone';

const propTypes = {
  albumId: PropTypes.string.isRequired,
  onContributorSendSuccess: PropTypes.func,
  onRequestClose: PropTypes.func.isRequired,
};

const STEPS = {
  PHONE: 'PHONE',
  PIN_VERIFY: 'PIN_VERIFY',
  NAME_INSERT: 'NAME_INSERT',
  SUCCESS: 'SUCCESS',
};

const AlbumSlideshowOnboarding = ({
  albumId,
  onContributorSendSuccess,
  onRequestClose,
}) => {
  const history = useHistory();
  const [step, setStep] = useState(STEPS.PHONE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSmsSent, setIsSmsSent] = useState(false);
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const { album, slideshow } = useAlbumSlideshow({ albumId });
  const {
    isContributor,
    onAccountUpdate,
    onAlbumSlideshowContributeSend,
    onAutoContributorInvitationsSend,
  } = useSlideshowOnboarding({ albumId });
  const { onTokenRequest } = useGoogleRecaptcha();
  const {
    loading: phoneAuthLoading,
    pinSent,
    requireHumanInput,
    blockLogin,
    onPhoneAuthSubmit,
    onPhoneAuthVerify,
    onResendPhoneAuthCode,
  } = usePhoneAuth();

  useEffect(() => {
    if (user?.name && user.name !== 'Me') {
      setStep(STEPS.SUCCESS);
    } else if (user) {
      setStep(STEPS.NAME_INSERT);
    } else {
      setStep(STEPS.PHONE);
    }
  }, [user]);

  useEffect(() => {
    if (pinSent && step !== STEPS.PIN_VERIFY) {
      setStep(STEPS.PIN_VERIFY);
    }
  }, [pinSent]);

  // We send the SMS regardless of whether or not the user has a name.

  const onAlbumContribute = async () => {
    if (authenticated && user && isContributor) {
      try {
        const { data: { albumSlideshowContributeSend } = {} } =
          await onAlbumSlideshowContributeSend();
        if (albumSlideshowContributeSend?.success) {
          setIsSmsSent(true);
        }
        onContributorSendSuccess?.();
      } catch (err) {
        setError(getErrorString(err));
      }
      //  If the user is not a contributor we should auto-join them first
    } else if (authenticated && user) {
      try {
        await onAutoContributorInvitationsSend(user.phone_number);
      } catch (err) {
        setError(getErrorString(err));
      }
    }
  };

  // We send the SMS regardless of whether or not the user has a name.
  useEffect(() => {
    if (authenticated && user) {
      onAlbumContribute();
    }
  }, [authenticated, user, isContributor]);

  const attemptPhoneVerificationRequest = async ({
    phone_number: phoneNumber,
    force_allow_action: forceAllowAction,
  }) => {
    setLoading(true);
    setError('');

    try {
      const token = await onTokenRequest({ action: 'login' });
      await onPhoneAuthSubmit(phoneNumber, true, token, forceAllowAction);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptPhoneLogin = async ({ phone_number: phoneNumber, code }) => {
    setLoading(true);
    setError('');

    try {
      await onPhoneAuthVerify(parsePhone(phoneNumber), code);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptPinCodeResend = async ({
    phone_number: phoneNumber,
    force_allow_action: forceAllowAction,
  }) => {
    try {
      const token = await onTokenRequest({ action: 'login' });
      await onResendPhoneAuthCode(phoneNumber, true, token, forceAllowAction);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptAccountUpdate = async ({ name }) => {
    setLoading(true);
    setError('');

    if (!user) {
      setError('Insufficient privileges');
      return;
    }

    try {
      await onAccountUpdate(user.id, name);
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const onLogin = async values => {
    if (step === STEPS.PHONE) {
      await attemptPhoneVerificationRequest(values);
    }

    if (step === STEPS.PIN_VERIFY) {
      await attemptPhoneLogin(values);
    }

    if (step === STEPS.NAME_INSERT) {
      await attemptAccountUpdate(values);
    }
  };

  if (!album || !slideshow || (authenticated && !user)) {
    return (
      <div className="w-100 my-6 py-6 d-flex align-items-center justify-content-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="px-lg-2 pb-3">
        <h2 className="text-center mb-4">
          {step === STEPS.NAME_INSERT && <Fragment>One more thing!</Fragment>}
          {step === STEPS.SUCCESS && <Fragment>Success!</Fragment>}
          {[STEPS.PHONE, STEPS.PIN_VERIFY].includes(step) && (
            <Fragment>
              <span className="text-sm text-uppercase text-light text-bold">
                WELCOME TO
              </span>{' '}
              <br />
              {album.name}
            </Fragment>
          )}
        </h2>

        {![STEPS.SUCCESS].includes(step) && (
          <Formik
            onSubmit={onLogin}
            initialValues={{
              email: '',
              password: '',
              phone_number: '+1',
              name: '',
              code: '',
            }}
            validateOnBlur={false}
            validateOnChange={false}
            render={({ values, handleSubmit, handleChange, handleBlur }) => {
              return (
                <>
                  {step === STEPS.PHONE && (
                    <PhoneForm
                      handleSubmit={handleSubmit}
                      loading={phoneAuthLoading}
                      handleChange={handleChange}
                      values={values}
                      requireHumanInput={requireHumanInput}
                      blockLogin={blockLogin}
                    />
                  )}
                  {step === STEPS.PIN_VERIFY && (
                    <PinVerifyForm
                      handleSubmit={handleSubmit}
                      loading={phoneAuthLoading}
                      handleChange={handleChange}
                      values={values}
                      requireHumanInput={requireHumanInput}
                      blockLogin={blockLogin}
                      onPinCodeResend={attemptPinCodeResend}
                    />
                  )}
                  {step === STEPS.NAME_INSERT && (
                    <NameForm
                      album={album}
                      handleSubmit={handleSubmit}
                      loading={loading}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      values={values}
                    />
                  )}
                  {error && (
                    <Alert type="danger" className="mt-2 mx-md-6 text-center">
                      {error}
                    </Alert>
                  )}
                </>
              );
            }}
          />
        )}
        {step === STEPS.SUCCESS && (
          <SuccessForm
            isSmsSent={isSmsSent}
            onCreateEvent={() => {
              history.push(`/app/shutterbug/new`);
              onRequestClose();
            }}
          />
        )}
      </div>
      <div className="mt-auto mb-4 mb-md-2 text-center">
        <a href="/terms" target="_blank">
          Terms &amp; Privacy
        </a>
        <p className="mt-1 mb-0 text-sm">
          This site is protected by reCAPTCHA.
        </p>
      </div>
    </Fragment>
  );
};

AlbumSlideshowOnboarding.propTypes = propTypes;

export default AlbumSlideshowOnboarding;
